import React from 'react';
import { useDeleteBoxesBulk } from '../hooks/useBoxesBulk';
import { useToastContext } from '../contexts/ToastContext';

interface BulkDeleteBoxesModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIds: string[];
}

const BulkDeleteBoxesModal: React.FC<BulkDeleteBoxesModalProps> = ({
  isOpen,
  onClose,
  selectedIds,
}) => {
  const { showSuccess, showError, showWarning, showInfo } = useToastContext();
  const deleteBoxesBulkMutation = useDeleteBoxesBulk();

  if (!isOpen) return null;

  const handleConfirm = async () => {
    try {
      await deleteBoxesBulkMutation.mutateAsync(selectedIds);
      showSuccess('Boxes supprimées', `${selectedIds.length} box(es) supprimée(s) avec succès.`);
      onClose();
    } catch (error: any) {
      console.error('Erreur:', error);
      showError('Erreur', error.response?.data?.message || error.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Supprimer {selectedIds.length} box(es)
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Êtes-vous sûr de vouloir supprimer ces {selectedIds.length} boxes mobiles ?
                  </p>
                </div>
                <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm font-medium text-red-800 mb-2">
                    ⚠️ Attention : Vérifications automatiques
                  </p>
                  <ul className="text-xs text-red-700 space-y-1 list-disc list-inside">
                    <li>La suppression sera bloquée s'il y a des objets dans les boxes</li>
                    <li>Cette action est irréversible</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={deleteBoxesBulkMutation.isPending}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
            >
              {deleteBoxesBulkMutation.isPending ? 'Suppression...' : 'Supprimer'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkDeleteBoxesModal;

